import { getLogger } from './logger-context';

export type ResolvedComponentScope = HTMLElement | Document | string;
export type UnresolvedComponentScope = ResolvedComponentScope | unknown;

/**
 * Resolves the DOM scope for component initialization and manipulation.
 * This function determines where component selectors will be applied.
 *
 * @param scope - Target element, CSS selector string, or null/undefined to use document
 * @returns The DOM element to use as scope for component initialization
 */
export function resolveComponentScope(
    scope: UnresolvedComponentScope,
): HTMLElement | Document {
    const logger = getLogger();

    // Use document for null or undefined
    if (scope == null) {
        return document;
    }

    // If string selector provided, attempt to find matching element
    if (typeof scope === 'string') {
        const targetElement = document.querySelector<HTMLElement>(`#${scope}`);
        // Fall back to document if selector doesn't match any element
        return targetElement || document;
    }

    // If valid HTMLElement provided, use it directly
    if (scope instanceof HTMLElement) {
        return scope;
    }

    // For any other value types, use document as fallback with warning
    logger.warn(
        'Invalid component scope provided. Expected a CSS selector string or HTMLElement. ' +
            'Using document as fallback scope for component initialization.',
    );
    return document;
}
